import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  CALLOUT_BY_ID,
  CALLOUT_PRIORITY_ORDER,
  isCalloutVisibleToUser,
} from 'lib/featureCallouts'

import { useAuthContext } from './useUserContext'
import { useViewAs } from './useViewAs'

interface FeatureCalloutsContextValue {
  activeCalloutId: string | null
  register: (id: string) => void
  unregister: (id: string) => void
  dismiss: (id: string) => void
}

const FeatureCalloutsContext = createContext<FeatureCalloutsContextValue>({
  activeCalloutId: null,
  register: () => {},
  unregister: () => {},
  dismiss: () => {},
})

export function ProvideFeatureCallouts({
  children,
}: {
  children: React.ReactNode
}) {
  const { currentUser, global, patchUserMutation } = useAuthContext()
  const viewAs = useViewAs()

  // Track which callout IDs are currently mounted in the tree
  const [mountedIds, setMountedIds] = useState<Set<string>>(new Set())

  // Track dismissed callout IDs -- seeded from user prefs
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set())

  // Seed dismissed IDs from user prefs when user changes
  useEffect(() => {
    if (currentUser?.prefs && 'dismissedCallouts' in currentUser.prefs) {
      const prefs = currentUser.prefs as { dismissedCallouts?: string[] }
      setDismissedIds(new Set(prefs.dismissedCallouts ?? []))
    } else {
      setDismissedIds(new Set())
    }
  }, [currentUser?.id])

  const register = useCallback((id: string) => {
    setMountedIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }, [])

  const unregister = useCallback((id: string) => {
    setMountedIds((prev) => {
      const next = new Set(prev)
      next.delete(id)
      return next
    })
  }, [])

  const dismiss = useCallback(
    (id: string) => {
      setDismissedIds((prev) => {
        const next = new Set(prev)
        next.add(id)
        return next
      })

      // View-as is read-only: hide locally, never persist to the target.
      if (currentUser && !viewAs) {
        const nextDismissed = [...new Set([...dismissedIds, id])]
        patchUserMutation.mutate([
          { op: 'add', path: '/prefs/dismissedCallouts', value: nextDismissed },
        ])
      }
    },
    [currentUser, viewAs, patchUserMutation, dismissedIds],
  )

  // Derive the single active callout ID
  const activeCalloutId = useMemo(() => {
    if (!currentUser) return null

    for (const id of CALLOUT_PRIORITY_ORDER) {
      if (!mountedIds.has(id)) continue
      if (dismissedIds.has(id)) continue

      const def = CALLOUT_BY_ID.get(id)
      if (!def) continue
      if (!isCalloutVisibleToUser(def, global)) continue

      return id
    }

    return null
  }, [mountedIds, currentUser, dismissedIds, global])

  const value = useMemo(
    () => ({ activeCalloutId, register, unregister, dismiss }),
    [activeCalloutId, register, unregister, dismiss],
  )

  return (
    <FeatureCalloutsContext.Provider value={value}>
      {children}
    </FeatureCalloutsContext.Provider>
  )
}

export const useFeatureCalloutsContext = () =>
  useContext(FeatureCalloutsContext)
